import * as Dialog from '@radix-ui/react-dialog';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { MultiUploadForm } from '../../document/components/MultiUploadForm';
import { useTranslation } from '../../../contexts/translation-context';
import { Document, OrderDocumentType } from 'src/types/enums';
import { linkCarrierCmrDocument } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { OrderDocumentTypeToTranslationKey } from '../../order/utils/document-validation';

interface Props {
  carrierId: number;
  orderId: string;
  /**
   * The document types this upload may cover, or null when the dialog is
   * closed. A single type names itself in the title; with more than one the
   * carrier uploads whatever they have and the analyzer files each part under
   * the type it detects.
   */
  types: OrderDocumentType[] | null;
  onClose: () => void;
  onUploaded: (type: OrderDocumentType) => Promise<void>;
}

/**
 * Controlled upload dialog. The candidate types are owned by the caller (a
 * per-document button, or the order list's single upload button) so the copy
 * can be precise about what is expected.
 */
export const CarrierDocumentUploadDialog: React.FC<Props> = ({ carrierId, orderId, types, onClose, onUploaded }) => {
  const { i18n } = useTranslation();

  // The endpoint wants a declared type; when the carrier is uploading against
  // several outstanding documents at once we send the first and let the
  // analyzer re-type each detected sub-document.
  const declaredType = types?.[0] ?? null;
  const isSingleType = types?.length === 1;

  const handleSubmit = async (documents: Document[]) => {
    // MultiUploadForm still calls onSubmit when every file upload failed; bail
    // out so we don't send an empty documentIds array the endpoint will reject.
    if (!documents.length || !declaredType) return;
    try {
      await linkCarrierCmrDocument(carrierId, {
        orderId,
        documentIds: documents.map((d) => d.id),
        type: declaredType,
      });
      toast.success(i18n('carrierPortal.orders.cmrUploaded'));
      onClose();
      await onUploaded(declaredType);
    } catch (err) {
      captureException(err);
      toast.error(
        i18n('carrierPortal.orders.cmrUploadFailed', {
          error: getDisplayError(err),
        }),
      );
    }
  };

  return (
    <Dialog.Root open={types !== null} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title className="heading-one mb-4">
            {isSingleType && declaredType
              ? i18n('carrierPortal.orders.uploadDocumentTitle', {
                  document: i18n(OrderDocumentTypeToTranslationKey[declaredType]),
                })
              : i18n('carrierPortal.orders.uploadDocumentsTitle')}
          </Dialog.Title>
          <p className="text-fg-muted mb-4">
            {isSingleType
              ? i18n('carrierPortal.orders.cmrUploadHint')
              : i18n('carrierPortal.orders.uploadDocumentsHint')}
          </p>
          <h2 className="heading-two mb-2">{i18n('upload.title')}</h2>
          <MultiUploadForm onSubmit={handleSubmit} onCancel={onClose} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
